import React, { useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useSubNodes, useNodeFullTree } from '../hooks/useNode';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import {
  Users,
  ExternalLink,
  ChevronRight,
  ShieldCheck,
  Sparkles,
  Layers,
  ListTree,
  Building2,
  Shield,
  ArrowRight
} from 'lucide-react';
import { NodeCreationWizard } from '../components/shell/NodeCreationWizard';
import { cn } from '../lib/utils';

type ViewMode = 'list' | 'tree';

/** Recursive tree renderer for the "Structure" view. Both views read the same
 *  /api/nodes/{id}/tree payload, they just present it differently. */
const TreeNode = ({ node }: { node: any }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const { switchOrg } = useTenant();
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="ml-4 first:ml-0">
      <div
        className={cn(
          "flex items-center gap-3 py-3 px-4 rounded-none transition-all mb-2 border-2 border-black dark:border-white border-transparent",
          "bg-white hover:border-blue-100 hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900"
        )}
      >
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn("p-1 rounded-none hover:bg-white transition-colors dark:hover:bg-slate-800", !hasChildren && "invisible")}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? <ChevronRight size={16} className="rotate-90" /> : <ChevronRight size={16} />}
        </button>

        <div
          className={cn(
            "w-10 h-10 rounded-none flex items-center justify-center text-white shrink-0 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]",
            node.parentId ? "bg-blue-600" : "bg-navy-900"
          )}
        >
          {node.parentId ? <Building2 size={20} /> : <Shield size={20} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-bold text-gray-900 dark:text-gray-100 truncate">{node.name}</h4>
            <Badge variant="blue" className="text-[10px] uppercase font-mono px-1">
              {node.tier}
            </Badge>
          </div>
          <p className="text-xs text-gray-500">/{node.slug}</p>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs gap-1.5"
          onClick={() => switchOrg(node.slug, '/dashboard')}
        >
          <span>Enter Org</span>
          <ArrowRight size={14} />
        </Button>
      </div>

      {isExpanded && hasChildren && (
        <div className="overflow-hidden border-l-2 border-black dark:border-white ml-5">
          {node.children.map((child: any) => (
            <TreeNode key={child.id} node={child} />
          ))}
        </div>
      )}
    </div>
  );
};

export const SubOrgsPage = () => {
  const { node, getTerm, switchOrg } = useTenant();
  const { data: subNodes = [], isLoading } = useSubNodes(node?.id || '');
  const { data: fullTree } = useNodeFullTree(node?.id || '');
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [view, setView] = useState<ViewMode>('list');

  const childTerm = getTerm('childNode');
  const memberTerm = getTerm('member');

  if (isLoading) {
    return <div className="flex items-center justify-center h-64">Loading nodes...</div>;
  }

  return (
    <div className="space-y-6">
      <NodeCreationWizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} />

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{childTerm} Management</h1>
          <p className="text-sm text-gray-500">
            {view === 'list'
              ? `Monitor and manage the ${childTerm.toLowerCase()}s in your lineage.`
              : 'Visualize the complete command and control structure across all units.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* List / Structure toggle — same /tree payload, two presentations. */}
          <div className="flex items-center border-2 border-black dark:border-white">
            <button
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
              className={cn(
                "flex items-center gap-2 px-3 py-2 text-xs font-mono font-black uppercase tracking-wider transition-colors",
                view === 'list'
                  ? 'bg-black text-white dark:bg-white dark:text-black'
                  : 'bg-white text-black hover:bg-[#FFD600] dark:bg-slate-900 dark:text-white'
              )}
            >
              <ListTree size={16} />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => setView('tree')}
              aria-pressed={view === 'tree'}
              className={cn(
                "flex items-center gap-2 px-3 py-2 text-xs font-mono font-black uppercase tracking-wider transition-colors border-l-2 border-black dark:border-white",
                view === 'tree'
                  ? 'bg-black text-white dark:bg-white dark:text-black'
                  : 'bg-white text-black hover:bg-[#FFD600] dark:bg-slate-900 dark:text-white'
              )}
            >
              <Layers size={16} />
              <span className="hidden sm:inline">Structure</span>
            </button>
          </div>
          <Button onClick={() => setIsWizardOpen(true)} className="bg-navy-900">
            <Sparkles size={18} />
            <span>New {childTerm} Wizard</span>
          </Button>
        </div>
      </div>

      {view === 'tree' ? (
        <Card className="p-8 bg-white/50 border-black dark:border-white dark:bg-slate-950">
          {fullTree ? (
            <TreeNode node={fullTree} />
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-white rounded-none flex items-center justify-center mx-auto mb-4 text-gray-400 dark:bg-slate-800">
                <Building2 size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">No tree data available</h3>
              <p className="text-sm text-gray-500">Contact support if your lineage is missing.</p>
            </div>
          )}
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {subNodes.map((subNode) => (
              <Card key={subNode.id} className="p-0 overflow-hidden border-2 border-black dark:border-white hover:border-blue-200 transition-all shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] hover:shadow-[4px_4px_0px_#000000] dark:hover:shadow-[4px_4px_0px_#FFFFFF] group">
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-none bg-navy-900 text-white flex items-center justify-center text-lg font-bold shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
                        {(subNode.name || '').substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-700 transition-colors">{subNode.name}</h3>
                        <p className="text-xs text-gray-500">/{subNode.slug}</p>
                      </div>
                    </div>
                    <Badge variant="blue" className="text-[10px] uppercase font-mono">Active</Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-6">
                    <div className="p-3 rounded-none bg-white border-2 border-black dark:border-white border-transparent group-hover:bg-blue-50/50 transition-colors dark:bg-slate-950">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono mb-1">{memberTerm}s</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{(subNode.memberCount || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 rounded-none bg-white border-2 border-black dark:border-white border-transparent group-hover:bg-blue-50/50 transition-colors dark:bg-slate-950">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono mb-1">Dues</p>
                      <p className="text-sm font-bold text-gray-500">--</p>
                    </div>
                    <div className="p-3 rounded-none bg-white border-2 border-black dark:border-white border-transparent group-hover:bg-blue-50/50 transition-colors dark:bg-slate-950">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono mb-1">Pulse</p>
                      <p className="text-sm font-bold text-gray-500">--</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      className="flex-1 text-xs h-9 justify-center gap-2"
                      onClick={() => switchOrg(subNode.slug, '/dashboard')}
                    >
                      <ShieldCheck size={14} />
                      Manage as Admin
                    </Button>
                    {/* Nodes live on the platform domain (orgsets.com) with the slug in the path:
                        /orgs/{slug} redirects into the node's dashboard. No per-org subdomains. */}
                    <a 
                      href={`${window.location.origin}/orgs/${subNode.slug}`} 
                      target="_blank" 
                      rel="noreferrer"
                      aria-label={`Open ${subNode.name} dashboard in new tab`}
                      className="h-9 w-9 inline-flex items-center justify-center rounded-none text-black hover:bg-black hover:text-white dark:text-white dark:hover:bg-white dark:hover:text-black border-2 border-transparent hover:border-black dark:hover:border-white transition-all font-mono"
                    >
                      <ExternalLink size={14} />
                      <span className="sr-only">Open {subNode.name}</span>
                    </a>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {subNodes.length === 0 && (
            <Card className="flex flex-col items-center justify-center p-12 text-center border-dashed border-2">
              <div className="w-16 h-16 rounded-none bg-white flex items-center justify-center text-gray-400 mb-4 dark:bg-slate-800">
                <Users size={32} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No {childTerm.toLowerCase()}s found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
                You haven't created any {childTerm.toLowerCase()}s yet. Use them to delegate authority to smaller teams or local branches.
              </p>
              <Button onClick={() => setIsWizardOpen(true)} className="bg-navy-900">
                <Sparkles size={18} />
                <span>Open Setup Wizard</span>
              </Button>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
